import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement } from 'react';
import { base64Encode, uuid } from '@loyalty/shared-common';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons/faSpinnerThird';
import { CampaignCardSkeleton } from './components/campaign-card/CampaignCardSkeleton';
import { CampaignCardsFeedSkeleton } from './components/campaign-cards-feed/CampaignCardsFeedSkeleton';
import { CurrencyCounterSkeleton } from './components/currency-counter-component/CurrencyCounterSkeleton';
import { InteractionsListSkeleton } from './components/interactions-list-component/InteractionsListSkeleton';
import { LeaderboardTableSkeleton } from './components/leaderboard-component/LeaderboardTableSkeleton';
import { LeaderboardV2TableSkeleton } from './components/leaderboard-v2-component/LeaderboardV2TableSkeleton';
import { LevelV2Skeleton } from './components/level-component-v2/LevelV2Skeleton';
import { LinkSkeleton } from './components/link-component/LinkSkeleton';
import { ProfileV2ComponentSkeleton } from './components/profile-component-v2/ProfileV2ComponentSkeleton';
import { QrCodeDataSkeleton } from './components/qr-code-data-component/QrCodeDataSkeleton';
import { ReferralDataSkeleton } from './components/referral-data-component/ReferralDataSkeleton';
import { RewardsListSkeleton } from './components/rewards-list-component/components/RewardsListSkeleton';
import { RewardsWonTableSkeleton } from './components/rewards-won-component/subcomponents/RewardsWonTableSkeleton';
import { RssSkeleton } from './components/rss-component/RssSkeleton';
import { TabsSkeleton } from './components/tabs-component/TabsSkeleton';
import { TransactionHistoryTableV2Skeleton } from './components/transaction-history-component-v2/TransactionHistoryTableV2Skeleton';
import { reactComponents } from './react-components';

const defaultLoader = (
  <div className="react_loader_container">
    <FontAwesomeIcon icon={faSpinnerThird} color="grey" spin className="react_loader" />
  </div>
);

interface Props<T extends keyof typeof reactComponents> {
  component: T;
  props: Parameters<(typeof reactComponents)[T]>[0];
  document?: Document; // Only useful inside editor
  componentType: string;
}

export function ReactBootstrap<T extends keyof typeof reactComponents>({
  component,
  props,
  document,
  componentType,
}: Props<T>): ReactElement {
  const componentId = props.id || uuid();
  const wrapperId = `react_${componentId}`;

  const base64Props = base64Encode(JSON.stringify(props));
  const innerBootstrapWrapper = (
    <>
      <div id={wrapperId} className="react_wrapper">
        <WidgetLoader component={component} props={props} />
      </div>

      <MyScript
        document={document}
        code={`
            setTimeout(function() {
              window._async_bind_react_widget = (window._async_bind_react_widget || []);
              window._async_bind_react_widget.push({id: '${wrapperId}', base64Props: '${base64Props}', component: '${component}'});
            }, 0);
        `}
      />
    </>
  );

  // Editor automatically adds a div, this behaviour is reproduced manually in the portal
  return document ? (
    innerBootstrapWrapper
  ) : (
    <div
      id={componentId}
      className={(props as any).attributes?.class}
      data-gjs-type={componentType}
    >
      {innerBootstrapWrapper}
    </div>
  );
}

function MyScript({ code, document: doc }: { code: string; document?: Document }) {
  if (!doc) {
    // PF
    return <script dangerouslySetInnerHTML={{ __html: code }} />;
  }

  // Editor
  const script = doc.createElement('script');
  script.innerHTML = code;
  doc.body.appendChild(script);

  return <></>;
}

interface WidgetLoaderProps<T extends keyof typeof reactComponents> {
  component: T;
  props: any;
}

function WidgetLoader<T extends keyof typeof reactComponents>({
  component,
  props,
}: WidgetLoaderProps<T>): ReactElement {
  // TODO: add a loader for each component
  switch (component) {
    case 'CampaignCard':
      return <CampaignCardSkeleton props={props} />;
    case 'CampaignCardsFeed':
      return <CampaignCardsFeedSkeleton props={props} />;
    case 'CurrencyCounterComponent':
      return <CurrencyCounterSkeleton />;
    case 'LeaderboardTableComponent':
      return <LeaderboardTableSkeleton />;
    case 'LeaderboardV2TableComponent':
      return <LeaderboardV2TableSkeleton />;
    case 'LevelV2Component':
      return <LevelV2Skeleton />;
    case 'ProfileV2Component':
      return <ProfileV2ComponentSkeleton />;
    case 'QrCodeComponent':
      return <QrCodeDataSkeleton />;
    case 'ReferralDataComponent':
      return <ReferralDataSkeleton />;
    case 'TransactionHistoryTableV2Component':
      return <TransactionHistoryTableV2Skeleton />;
    case 'InteractionsListComponent':
      return <InteractionsListSkeleton />;
    case 'RewardsListComponent':
      return <RewardsListSkeleton />;
    case 'RssComponent':
      return <RssSkeleton />;
    case 'RewardsWonTableComponent':
      return <RewardsWonTableSkeleton />;
    case 'LinkComponent':
      return <LinkSkeleton />;
    case 'TabsComponent':
      return <TabsSkeleton />;
    default:
      return defaultLoader;
  }
}
